import React, {
  forwardRef,
  InputHTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  FocusEvent,
  useImperativeHandle,
  useRef,
} from 'react';

type DateInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'maxLength'>;

const DATE_SEGMENTS = [
  { start: 0, end: 2 },
  { start: 3, end: 5 },
  { start: 6, end: 10 },
];

const isFullDate = (value: string) => /^\d{2}\/\d{2}\/\d{4}$/.test(value);

const formatDateValue = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const getSegmentForPosition = (position: number) => {
  if (position <= 2) {
    return DATE_SEGMENTS[0];
  }
  if (position <= 5) {
    return DATE_SEGMENTS[1];
  }
  return DATE_SEGMENTS[2];
};

const normalizeEditablePosition = (position: number) => {
  if (position === 2) {
    return 3;
  }
  if (position === 5) {
    return 6;
  }
  return Math.min(Math.max(position, 0), 9);
};

const getNextSelection = (position: number) => {
  if (position === 1) {
    return { start: 3, end: 5 };
  }
  if (position === 4) {
    return { start: 6, end: 10 };
  }
  if (position >= 9) {
    return { start: 10, end: 10 };
  }
  return { start: position + 1, end: position + 2 };
};

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ value = '', onChange, onKeyDown, onClick, onFocus, onPaste, placeholder = 'dd/mm/aaaa', ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const stringValue = String(value ?? '');

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const setSelection = (start: number, end: number) => {
      window.requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(start, end);
      });
    };

    const emitChange = (
      nextValue: string,
      event: React.ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>
    ) => {
      if (!onChange) {
        return;
      }

      event.currentTarget.value = nextValue;
      (event.target as HTMLInputElement).value = nextValue;
      onChange(event as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    const selectSegment = () => {
      const input = inputRef.current;
      if (!input || !isFullDate(input.value)) {
        return;
      }

      const position = input.selectionStart ?? 0;
      const segment = getSegmentForPosition(position);
      setSelection(segment.start, segment.end);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      emitChange(formatDateValue(event.target.value), event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || props.readOnly || props.disabled) {
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      if (!/^\d$/.test(event.key)) {
        return;
      }

      const input = event.currentTarget;
      if (!isFullDate(input.value)) {
        return;
      }

      event.preventDefault();

      const rawStart = input.selectionStart ?? 0;
      const rawEnd = input.selectionEnd ?? rawStart;
      const segment = getSegmentForPosition(rawStart);
      const selectedWholeSegment = rawStart === segment.start && rawEnd === segment.end;
      const replacePosition = selectedWholeSegment ? segment.start : normalizeEditablePosition(rawStart);
      const nextValue = `${input.value.slice(0, replacePosition)}${event.key}${input.value.slice(replacePosition + 1)}`;
      const nextSelection = getNextSelection(replacePosition);

      emitChange(nextValue, event);
      setSelection(nextSelection.start, nextSelection.end);
    };

    const handleClick = (event: MouseEvent<HTMLInputElement>) => {
      onClick?.(event);
      selectSegment();
    };

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);
      selectSegment();
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(event);
      if (event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      const pastedValue = formatDateValue(event.clipboardData.getData('text'));
      if (!onChange) {
        return;
      }

      const target = event.currentTarget;
      target.value = pastedValue;
      onChange(event as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <input
        {...props}
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={10}
        placeholder={placeholder}
        value={stringValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        onFocus={handleFocus}
        onPaste={handlePaste}
      />
    );
  }
);

export default DateInput;
